/**
 * The controller, read the way the keyboard is: as a set of held verbs.
 *
 * The browser's Gamepad API is a poll, not a stream: `navigator.getGamepads()`
 * hands back a snapshot of every button and axis, and nothing is ever pushed.
 * So this is two halves, like the keyboard's. `resolvePad` is pure: a snapshot
 * in, the game's own vocabulary out (a move vector, a look delta, the actions
 * held, the triggers). `createGamepad` is the part that touches the browser:
 * it polls, keeps the previous frame for edges, and rumbles.
 *
 * Plug and play: whichever pad last did anything is the pad, standard mapping
 * only (every Xbox pad on every browser that matters reports it), and a pad
 * that is unplugged mid-shift just stops contributing. Nothing here is ever
 * required; a player with no pad never runs any of it.
 *
 * The layout is one row per verb, the same verbs `bindings.json` names, so the
 * pad is a second keyboard and not a second game. See `PAD_LAYOUT`.
 */

/** Standard-mapping button indices (W3C "standard" gamepad). */
export const PAD = Object.freeze({
  A: 0, B: 1, X: 2, Y: 3,
  LB: 4, RB: 5, LT: 6, RT: 7,
  VIEW: 8, MENU: 9, LS: 10, RS: 11,
  UP: 12, DOWN: 13, LEFT: 14, RIGHT: 15,
});

/**
 * Which button is which verb.
 *
 * A does what E does, tapped and held, because the action key is the one key
 * the whole game is built around and it belongs under the thumb. B backs out.
 * X is the throw, held to wind up and let go to throw, like G. Y jumps. The
 * sheet is held on the left bumper the way F is held, and the right bumper
 * files the room, R's one job. View is the controls card, Menu is pause.
 * Left stick click runs, the way every pad game runs. The d-pad is a menu's:
 * up and down move through a list, left and right change a setting, and out in
 * the world left and right are the time scale, which is a dev key on the
 * keyboard as well.
 */
export const PAD_LAYOUT = Object.freeze({
  interact: 'A',
  back: 'B',
  throw: 'X',
  jump: 'Y',
  sheet: 'LB',
  submit: 'RB',
  sprint: 'LS',
  controls: 'VIEW',
  pause: 'MENU',
  debug: 'RS',
});

/** What the screen calls each button, Xbox names. */
export const PAD_NAMES = Object.freeze({
  A: 'A', B: 'B', X: 'X', Y: 'Y',
  LB: 'LB', RB: 'RB', LT: 'LT', RT: 'RT',
  VIEW: 'View', MENU: 'Menu', LS: 'L3', RS: 'R3',
  UP: 'D-pad up', DOWN: 'D-pad down', LEFT: 'D-pad left', RIGHT: 'D-pad right',
});

export const PAD_TUNING = Object.freeze({
  /** Below this a stick is resting. Radial, so diagonals are not cut short. */
  deadzone: 0.18,
  /** A trigger is pressed past this. Triggers rest a hair off zero on some pads. */
  trigger: 0.12,
  /** Radians a second at full right stick, at a sensitivity of 1. */
  turnRate: 3.2,
  /** Pitch is slower than yaw: you look round far more than you look up. */
  pitchRate: 2.0,
  /** The fixed step the loop runs at; the look delta is per frame. */
  dt: 1 / 60,
});

/**
 * Moving round a menu: what counts as a push, and how a held one repeats.
 *
 * The d-pad or the left stick. The stick has to be pushed past `stick` on its
 * main axis to count (so a stick resting a little off centre, or pushed on a
 * diagonal, does not wander round a menu) and let back under `release` before
 * it counts again. Held, a direction fires once, waits `delay` seconds, then
 * fires every `every` seconds, which is the rhythm every console menu has.
 */
export const MENU_NAV = Object.freeze({ stick: 0.6, release: 0.35, delay: 0.4, every: 0.11 });

/** How fast the right stick scrolls a panel at a full push, pixels a second. */
export const SCROLL_SPEED = 1100;

/**
 * A stick, with the deadzone taken out and the rest stretched back to 0..1.
 *
 * Radial rather than per axis: a per-axis deadzone snaps a nearly-forward push
 * to exactly forward and makes the diagonals a cross.
 */
export function deadzone(x, y, zone = PAD_TUNING.deadzone) {
  const length = Math.hypot(x, y);
  if (length <= zone) return { x: 0, y: 0 };
  const scaled = Math.min(1, (length - zone) / (1 - zone));
  return { x: (x / length) * scaled, y: (y / length) * scaled };
}

const value = (button) => (typeof button === 'object' ? button?.value ?? (button?.pressed ? 1 : 0) : button ? 1 : 0);
const down = (button) => (typeof button === 'object' ? Boolean(button?.pressed) || value(button) > 0.5 : Boolean(button));

/**
 * One snapshot of one pad, in the game's words.
 *
 * `settings` carries the player's choices: `sensitivity` (1 is the default)
 * and `invertY`. The look delta is in radians for this frame, signed the way
 * the mouse's is, so the camera cannot tell which one turned it.
 */
export function resolvePad(pad, settings = {}) {
  if (!pad || !pad.connected) return null;
  const buttons = pad.buttons ?? [];
  const axes = pad.axes ?? [];
  const sensitivity = settings.sensitivity ?? 1;
  const invert = settings.invertY ? -1 : 1;

  const left = deadzone(axes[0] ?? 0, axes[1] ?? 0);
  const right = deadzone(axes[2] ?? 0, axes[3] ?? 0);
  // A gentle curve on the look stick: small pushes are fine aim, a full push
  // is a full turn. Movement stays linear, because walking speed is the stick.
  const curve = (v) => Math.sign(v) * v * v;

  const held = new Set();
  for (const [verb, name] of Object.entries(PAD_LAYOUT)) {
    if (down(buttons[PAD[name]])) held.add(verb);
  }

  const gas = value(buttons[PAD.RT]);
  const brake = value(buttons[PAD.LT]);
  const pressed = (index) => down(buttons[index]);

  return {
    // +y forward: the stick's up is negative on the standard mapping.
    move: { x: left.x, y: -left.y },
    look: {
      x: curve(right.x) * PAD_TUNING.turnRate * sensitivity * PAD_TUNING.dt,
      y: curve(right.y) * PAD_TUNING.pitchRate * sensitivity * invert * PAD_TUNING.dt,
    },
    held,
    gas: gas > PAD_TUNING.trigger ? gas : 0,
    brake: brake > PAD_TUNING.trigger ? brake : 0,
    dpad: {
      up: pressed(PAD.UP),
      down: pressed(PAD.DOWN),
      left: pressed(PAD.LEFT),
      right: pressed(PAD.RIGHT),
    },
    confirm: pressed(PAD.A),
    cancel: pressed(PAD.B),
    /** The left stick as it is, past the deadzone, +y up: for menus. */
    stick: { x: left.x, y: -left.y },
    /** The right stick past the deadzone, +y down the page: for scrolling a panel. */
    scroll: { x: right.x, y: right.y },
    /** Anything at all past rest, which is what makes the pad the device in use. */
    active:
      left.x !== 0 || left.y !== 0 || right.x !== 0 || right.y !== 0
      || gas > PAD_TUNING.trigger || brake > PAD_TUNING.trigger
      || buttons.some((button, index) => index !== PAD.LT && index !== PAD.RT && down(button)),
  };
}

const NOTHING = Object.freeze({
  move: Object.freeze({ x: 0, y: 0 }),
  look: Object.freeze({ x: 0, y: 0 }),
  held: new Set(),
  gas: 0,
  brake: 0,
  dpad: Object.freeze({ up: false, down: false, left: false, right: false }),
  confirm: false,
  cancel: false,
  stick: Object.freeze({ x: 0, y: 0 }),
  scroll: Object.freeze({ x: 0, y: 0 }),
  active: false,
});

/**
 * Which menu directions are being pushed in a reading, by the d-pad or the
 * left stick. `was` is last frame's set, for the stick's release threshold.
 */
export function menuDirections(state, was = new Set()) {
  const held = new Set();
  const dpad = state.dpad ?? {};
  for (const name of ['up', 'down', 'left', 'right']) if (dpad[name]) held.add(name);
  const { x, y } = state.stick ?? { x: 0, y: 0 };
  const ax = Math.abs(x);
  const ay = Math.abs(y);
  const vertical = ay >= ax;
  const amount = vertical ? ay : ax;
  const name = vertical ? (y > 0 ? 'up' : 'down') : x > 0 ? 'right' : 'left';
  if (amount >= MENU_NAV.stick || (amount >= MENU_NAV.release && was.has(name))) held.add(name);
  return held;
}

/**
 * The browser half: polls, picks the pad, keeps last frame for edges.
 *
 * `getPads` is injectable so a test (or a headless run with a pretend pad) can
 * hand it anything shaped like `navigator.getGamepads()`.
 */
export function createGamepad({ getPads = () => globalThis.navigator?.getGamepads?.() ?? [], settings = () => ({}) } = {}) {
  let state = NOTHING;
  let previous = NOTHING;
  let pad = null;
  let connected = false;
  /** Menu directions: how long each has been held, and which fired this frame. */
  const heldFor = { up: -1, down: -1, left: -1, right: -1 };
  let navHeld = new Set();
  let navFired = new Set();

  function stepNav(dt) {
    navHeld = menuDirections(state, navHeld);
    navFired = new Set();
    for (const name of Object.keys(heldFor)) {
      if (!navHeld.has(name)) {
        heldFor[name] = -1;
        continue;
      }
      const before = heldFor[name];
      const now = before < 0 ? 0 : before + dt;
      heldFor[name] = now;
      if (before < 0) navFired.add(name);
      else if (now >= MENU_NAV.delay) {
        const ticks = (t) => Math.floor((t - MENU_NAV.delay) / MENU_NAV.every);
        if (before < MENU_NAV.delay || ticks(now) > ticks(before)) navFired.add(name);
      }
    }
  }

  function pick() {
    let pads;
    try {
      pads = [...(getPads() ?? [])].filter((p) => p && p.connected);
    } catch {
      pads = [];
    }
    if (!pads.length) return null;
    // Keep the one we had if it is still there; otherwise the first standard one.
    const same = pad && pads.find((p) => p.index === pad.index);
    return same ?? pads.find((p) => p.mapping === 'standard') ?? pads[0];
  }

  /** Edges of a boolean read, this frame against last. */
  const edge = (read) => read(state) && !read(previous);

  return {
    poll() {
      previous = state;
      pad = pick();
      connected = Boolean(pad);
      state = (pad && resolvePad(pad, settings())) || NOTHING;
      stepNav(PAD_TUNING.dt);
      return state;
    },
    /**
     * Whether a menu should move this way this frame: the first push, and then
     * the held repeat (`MENU_NAV`), from the d-pad or the left stick.
     */
    nav: (direction) => navFired.has(direction),
    get state() {
      return state;
    },
    get connected() {
      return connected;
    },
    /** The verbs that went down this frame. */
    pressedVerbs() {
      return [...state.held].filter((verb) => !previous.held.has(verb));
    },
    up: () => edge((s) => s.dpad.up),
    down: () => edge((s) => s.dpad.down),
    left: () => edge((s) => s.dpad.left),
    right: () => edge((s) => s.dpad.right),
    confirm: () => edge((s) => s.confirm),
    cancel: () => edge((s) => s.cancel),
    pause: () => edge((s) => s.held.has('pause')),
    /**
     * A short buzz, where the pad can. Never required and never awaited: a pad
     * without an actuator, or a browser that refuses, simply does not buzz.
     */
    rumble(strength = 0.5, ms = 120) {
      const actuator = pad?.vibrationActuator;
      if (!actuator?.playEffect) return false;
      try {
        actuator
          .playEffect('dual-rumble', {
            duration: ms,
            strongMagnitude: Math.min(1, strength),
            weakMagnitude: Math.min(1, strength * 0.6),
          })
          ?.catch?.(() => {});
        return true;
      } catch {
        return false;
      }
    },
  };
}
